import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from './icons';
import MasterFields from './MasterFields';
import ZipModal from './ZipModal';
import DocViewModal, { DocViewState } from './DocViewModal';
import { buildCollect } from './collect';
import { ensuredMaster } from './master';
import { regenDocs } from './docgen';
import * as api from './api';
import type { Project, Master, Collect } from './types';

interface ExtraCtx {
  m: Master;
  collect: Collect;
  editM: (fn: (mm: Master) => void) => void;
  onDocView: (d: DocViewState) => void;
}

interface Props {
  p: Project;
  onPatch: (patch: Partial<Project>) => void;
  toast: (m: string) => void;
  withAnswers: boolean;
  extra?: (ctx: ExtraCtx) => React.ReactNode;
  regenLabel?: string;
  regenToast?: string;
  regenKeys?: string[];
}

export default function MasterPanel({ p, onPatch, toast, withAnswers, extra, regenLabel, regenToast, regenKeys }: Props) {
  const [genBusy, setGenBusy] = useState(false);
  const collect = useMemo<Collect>(() => p.collect ?? buildCollect(p), [p]);
  const master = useMemo<Master>(() => ensuredMaster(p, collect), [p, collect]);

  useEffect(() => {
    const patch: Partial<Project> = {};
    if (!p.collect) patch.collect = collect;
    if (JSON.stringify(p.master) !== JSON.stringify(master)) patch.master = master;
    if (Object.keys(patch).length) onPatch(patch);
  }, [p, collect, master, onPatch]);

  const editM = (fn: (m: Master) => void) => {
    const m = JSON.parse(JSON.stringify(master)) as Master;
    fn(m);
    onPatch({ master: m });
  };

  // GP 법인 설립연도(Popbill, companies 캐시) → 주요경력 자동: 빈값/자동형식일 때만 갱신
  const careerRef = useRef<string>('');
  useEffect(() => {
    const gpCo = collect.cos.find((x) => x.rep === 'gp');
    const bizno = ((gpCo && gpCo.bizno) || '').replace(/[^0-9]/g, '');
    const coNm = (gpCo && gpCo.name) || '';
    if (bizno.length !== 10 || !coNm || careerRef.current === bizno) return;
    careerRef.current = bizno;
    api.fetchBizYear(bizno).then((r) => {
      const yr = r && r.established_year;
      if (!yr) return;
      const want = '(' + yr + '~현재) ' + coNm + ' 대표이사';
      const cur = master.gp.career || '';
      const isAuto = cur === '' || /^\(.*~현재\)\s.*대표이사$/.test(cur);
      if (isAuto && cur !== want) editM((mm) => { mm.gp.career = want; });
    }).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collect, master.gp.career]);

  // 신청일(작성일) — 전 서류의 작성일·신청일 기준값.
  // 개업일(사업자등록일)이 비어 있거나 직전 신청일과 같으면(=수동 변경 전) 함께 이동시킨다.
  const setApplyDate = (v: string) => editM((mm) => {
    const prev = mm.applyDate || '';
    mm.applyDate = v;
    if (mm.s4 && (!mm.s4.openDate || mm.s4.openDate === prev)) mm.s4.openDate = v;
  });

  const [zipTarget, setZipTarget] = useState<'org' | 'gp' | null>(null);
  const [doc, setDoc] = useState<DocViewState | null>(null);

  // 직접 업로드(날인본·수정본·PDF교체)한 문서 = 최신 버전 kind가 'auto'가 아니고 실파일 보유 → 재생성에서 제외 가능
  const isUploaded = (k: string): boolean => {
    const vers = (p.docver && p.docver[k]) || [];
    const last = vers[vers.length - 1];
    return !!(last && last.kind !== 'auto' && (last.s3doc || last.s3pdf));
  };
  const keys = regenKeys || [];
  const uploadedKeys = keys.filter(isUploaded);
  const cleanKeys = keys.filter((k) => !isUploaded(k));
  const runRegen = async (ks: string[]) => {
    if (!regenLabel) onPatch({ s3gen: 1 });
    if (ks.length) {
      setGenBusy(true);
      try { await regenDocs(p, ks, onPatch, toast); } catch (e) { /* regenDocs가 토스트 처리 */ } finally { setGenBusy(false); }
    } else {
      toast(regenToast || '마스터값으로 문서 재생성됨');
    }
  };

  return (
    <div className="mpanel-b">
      <MasterFields p={p} collect={collect} m={master} withAnswers={withAnswers} editM={editM} onZip={setZipTarget} onDocView={setDoc} />
      {extra ? extra({ m: master, collect, editM, onDocView: setDoc }) : null}
      <div className="mgrp">기타</div>
      <div className="mgrid">
        <div className="mf">
          <label>신청일(작성일)</label>
          <input value={master.applyDate || ''} onChange={(e) => setApplyDate(e.target.value)} placeholder="YYYY-MM-DD" />
        </div>
      </div>
      <div className="mcalc">이 날짜가 생성 서류 전체의 작성일·신청일 기준이 됩니다 · 사업자등록 신청서의 신청일과 개업일(사업자등록일)에도 함께 반영</div>
      {uploadedKeys.length > 0 && cleanKeys.length > 0 ? (
        <>
          <button className="sendbtn" disabled={genBusy} onClick={() => runRegen(keys)}
            title={'직접 올리신(서명완료·수정본 포함) ' + uploadedKeys.length + '종까지 전부 새 자동생성본으로 덮어씁니다'}>
            <Icon n="doc" style={{ width: 14, height: 14 }} /> {genBusy ? '문서 생성 중…' : '🔄 전체 재생성 (' + keys.length + '종 · 업로드 덮어씀)'}
          </button>
          <button className="sendbtn" disabled={genBusy} onClick={() => runRegen(cleanKeys)}
            style={{ background: '#fff', border: '1px solid var(--accent)', color: 'var(--accent)' }}
            title={'직접 업로드·서명완료한 ' + uploadedKeys.length + '종은 그대로 두고 나머지 ' + cleanKeys.length + '종만 재생성합니다'}>
            📄 {genBusy ? '생성 중…' : '업로드·서명완료 제외 재생성 (' + cleanKeys.length + '종)'}
          </button>
        </>
      ) : (
        <button className="sendbtn" disabled={genBusy} onClick={() => runRegen(keys)}>
          <Icon n="doc" style={{ width: 14, height: 14 }} /> {genBusy ? '문서 생성 중… (건당 2~4초)' : (regenLabel || '마스터값으로 4개 문서 재생성')}
        </button>
      )}
      {zipTarget ? (
        <ZipModal
          onPick={(addr, zip) => {
            editM((m) => {
              if (zipTarget === 'org') { m.addr = addr; m.zip = zip; }
              else { m.gp.addr = addr; m.gp.zip = zip; }
            });
            setZipTarget(null);
            toast('주소·우편번호 입력됨');
          }}
          onClose={() => setZipTarget(null)}
        />
      ) : null}
      {doc ? <DocViewModal doc={doc} collect={collect} master={master} editM={editM} onClose={() => setDoc(null)} /> : null}
    </div>
  );
}
